import { shippingLabelEndpointPrefix } from '../../../config';
import { ProductData } from './productParser';

const API_BASE_URL = `${shippingLabelEndpointPrefix}/api/labeling/customer/dropshipping`;

export class AuthenticationError extends Error {
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * API service for product-related operations
 */

/**
 * Fetches the default product for dropshipping calculations
 * This product is shown to all users (logged in or not) as a demo/example
 */
export const fetchDefaultProduct = async (): Promise<ProductData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/demo-product`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch default product: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching default product:', error);

    // Return fallback default product if API fails
    // This matches the first demo product (银项链) from backend constants
    return {
      product_id: -1,
      user_id: 0,
      picture: null,
      name: '银项链',
      description: '925银材质，时尚饰品，用于日常佩戴，HS Code 711311',
      weight: 0.22,
      length: 6,
      width: 4,
      height: 1.5,
      dangerous_products: false,
      flammable_products: false,
      export_restricted_products: false,
      battery_level: 'NONE' as any,
      magnetic_level: 'NONE' as any,
      if_extra_packaging: false,
      other_complicated_handling: null,
      categories: ['金属制品'],
      sensitivity_level: 'LOW' as any
    };
  }
};

/**
 * Fetches all available demo products for dropshipping calculations
 * This returns multiple demo products for users to explore different scenarios
 */
export const fetchDemoProducts = async (): Promise<ProductData[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/demo-products`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch demo products: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching demo products:', error);

    // Return fallback demo products if API fails
    // These match the backend constants for consistency
    return [
      {
        product_id: -1,
        user_id: 0,
        picture: null,
        name: '银项链',
        description: '925银材质，时尚饰品，用于日常佩戴，HS Code 711311',
        weight: 0.22,
        length: 6,
        width: 4,
        height: 1.5,
        dangerous_products: false,
        flammable_products: false,
        export_restricted_products: false,
        battery_level: 'NONE' as any,
        magnetic_level: 'NONE' as any,
        if_extra_packaging: false,
        other_complicated_handling: null,
        categories: ['金属制品'],
        sensitivity_level: 'LOW' as any
      },
      {
        product_id: -2,
        user_id: 0,
        picture: null,
        name: '学生双肩书包',
        description: '涤纶材质学生书包，不含电子元件，HS Code 420292',
        weight: 1.98,
        length: 18,
        width: 12,
        height: 6,
        dangerous_products: false,
        flammable_products: false,
        export_restricted_products: false,
        battery_level: 'NONE' as any,
        magnetic_level: 'NONE' as any,
        if_extra_packaging: false,
        other_complicated_handling: null,
        categories: ['纺织品'],
        sensitivity_level: 'LOW' as any
      },
      {
        product_id: -3,
        user_id: 0,
        picture: null,
        name: '32英寸LED电视',
        description: 'HS Code 852872',
        weight: 18.5,
        length: 32,
        width: 20,
        height: 6,
        dangerous_products: false,
        flammable_products: false,
        export_restricted_products: false,
        battery_level: 'NONE' as any,
        magnetic_level: 'WEAK' as any,
        if_extra_packaging: false,
        other_complicated_handling: null,
        categories: ['电子整机'],
        sensitivity_level: 'MEDIUM' as any
      }
    ];
  }
};


/**
 * Fetches all products for the authenticated user
 */
export const fetchUserProducts = async (): Promise<ProductData[]> => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch user products: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching user products:', error);
    if (error instanceof AuthenticationError) {
      throw error;
    }
    return [];
  }
};

/**
 * Fetches a single product by ID
 */
export const getProduct = async (productId: number): Promise<ProductData> => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Creates a new product for the authenticated user
 */
export const createProduct = async (productData: Omit<ProductData, 'product_id' | 'user_id'>): Promise<ProductData> => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers,
      body: JSON.stringify(productData),
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorData.description || response.statusText;
      } catch (e) {
        // Ignore JSON parse error
      }
      const error: any = new Error(`Failed to create product: ${errorMessage}`);
      error.response = { status: response.status, data: { message: errorMessage } };
      throw error;
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Updates an existing product
 */
export const updateProduct = async (productId: number, productData: Partial<ProductData>): Promise<ProductData> => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(productData),
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorData.description || response.statusText;
      } catch (e) {
        // Ignore JSON parse error
      }
      const error: any = new Error(`Failed to update product: ${errorMessage}`);
      error.response = { status: response.status, data: { message: errorMessage } };
      throw error;
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Deletes a product
 */
export const deleteProduct = async (productId: number): Promise<void> => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorData.description || response.statusText;
      } catch (e) {
        // Ignore JSON parse error
      }
      const error: any = new Error(`Failed to delete product: ${errorMessage}`);
      error.response = { status: response.status, data: { message: errorMessage } };
      throw error;
    }
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

/**
 * Helper function to check if user is logged in
 */
export const isUserLoggedIn = (): boolean => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

